/**
 * UpgradeMedicPage — Thin orchestrator component.
 *
 * Wires 8 custom hooks to 6 containers, 3 modals, and sticky action bars.
 * All state and business logic lives in hooks; this component is layout only.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AppLayout, Box, Button, Container, Header, SpaceBetween, StatusIndicator, Tabs } from '@amzn/awsui-components-core';
import { HomePageSideNavigationV2 } from '../../side-navigation-v2/HomePageSideNavigationV2';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { eLocalStorageKeys } from 'src/constants/AppConstants';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import { DeploymentOptions, JobRun, SelectedVersions } from './types';
import { getEnabledConfigs, RUNTIME_CONFIGS, getLambdaCountByLanguage } from './config';
import { createLambdaColumnDefinitions, createLambdaColumnDefinitionsWithSelect } from './utils/columnDefinitions';
import styles from './styles.module.scss';

// Hooks
import { useInventoryData } from './hooks/useInventoryData';
import { useResourceSelection } from './hooks/useResourceSelection';
import { useResourceFiltering } from './hooks/useResourceFiltering';
import { useAgentPanel } from './hooks/useAgentPanel';
import { useJobTracking } from './hooks/useJobTracking';
import { useCloudWatchPolling } from './hooks/useCloudWatchPolling';
import { useBrazilEOLSelection } from './hooks/useBrazilEOLSelection';
import { useWebSocketConnection } from './hooks/useWebSocketConnection';
import { useWebSocketReconnect } from './hooks/useWebSocketReconnect';

// Containers
import { DashboardStatsContainer } from './containers/DashboardStatsContainer';
import { JobExecutionsContainer } from './containers/JobExecutionsContainer';
import { EOLRuntimesContainer } from './containers/EOLRuntimesContainer';
import { ResourceTableContainer } from './containers/ResourceTableContainer';
import { BrazilEOLContainer } from './containers/BrazilEOLContainer';
import { AgentPanelContainer } from './containers/AgentPanelContainer';

// Modals
import { VersionSelectionModal } from './modals/VersionSelectionModal';
import { InstallInstructionsModal } from './modals/InstallInstructionsModal';
import { BrazilVersionModal } from './modals/BrazilVersionModal';

export const UpgradeMedicMain: React.FC = () => {
  const authData = useAuth();
  const userAlias = authData?.Alias || 'user';

  // Dark mode — replaces the old polling approach with usePersistedState
  const [isDarkMode] = usePersistedState<boolean>(eLocalStorageKeys.IsDarkModeEnabled, true);

  // ── Modal state (local to orchestrator) ──
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<SelectedVersions>({});
  const [deploymentOptions, setDeploymentOptions] = useState<DeploymentOptions>({ deployToAlpha: false });
  const [concurrentJobAlert, setConcurrentJobAlert] = useState<string | null>(null);

  // Auto-dismiss concurrent job alert after 5 seconds
  useEffect(() => {
    if (!concurrentJobAlert) return;
    const timer = setTimeout(() => setConcurrentJobAlert(null), 8000);
    return () => clearTimeout(timer);
  }, [concurrentJobAlert]);

  // ── Hooks ──
  const inventory = useInventoryData();
  const selection = useResourceSelection(inventory.accounts);
  const filtering = useResourceFiltering(inventory.accounts, inventory.precomputedData);
  const agentPanel = useAgentPanel();
  const wsRef = useRef<WebSocket | null>(null);
  const wsMapRef = useRef<Map<string, WebSocket>>(new Map());

  const jobTracking = useJobTracking(
    userAlias,
    inventory.accounts,
    inventory.allLambdas,
    inventory.currentConfig.name,
    agentPanel.addLog,
    wsRef,
    wsMapRef
  );

  const cloudWatch = useCloudWatchPolling(
    agentPanel.isAgentPanelVisible,
    agentPanel.setAgentLogs,
    jobTracking.setJobHistory,
    agentPanel.setProgressMessage
  );

  const brazilEOL = useBrazilEOLSelection();

  const webSocket = useWebSocketConnection(
    {
      userAlias,
      selectedLambdas: selection.selectedLambdas,
      selectedVersions,
      deploymentOptions,
      getSelectedPipelines: selection.getSelectedPipelines,
      clearSelection: selection.clearSelection,
      currentConfigName: inventory.currentConfig.name,
      accounts: inventory.accounts,
      // Agent panel
      addLog: agentPanel.addLog,
      addPipelineLog: agentPanel.addPipelineLog,
      setIsAgentPanelVisible: agentPanel.setIsAgentPanelVisible,
      setJobStatus: agentPanel.setJobStatus,
      jobStatus: agentPanel.jobStatus,
      setProgressMessage: agentPanel.setProgressMessage,
      setStreamingText: agentPanel.setStreamingText,
      streamingTextRef: agentPanel.streamingTextRef,
      setPersistedEphemeralEntries: agentPanel.setPersistedEphemeralEntries,
      setPipelinePersistedEphemeral: agentPanel.setPipelinePersistedEphemeral,
      setPipelineStreamingText: agentPanel.setPipelineStreamingText,
      pipelineStreamingTextRef: agentPanel.pipelineStreamingTextRef,
      setParallelPipelines: agentPanel.setParallelPipelines,
      setPipelineLogs: agentPanel.setPipelineLogs,
      setActivePipelineTab: agentPanel.setActivePipelineTab,
      parallelPipelines: agentPanel.parallelPipelines,
      // Job tracking
      setJobHistory: jobTracking.setJobHistory,
      setCurrentJobId: jobTracking.setCurrentJobId,
      currentJobIdRef: jobTracking.currentJobIdRef,
      isJobRunningRef: jobTracking.isJobRunningRef,
      jobHistory: jobTracking.jobHistory,
      // Modals
      setIsInstallModalOpen
    },
    wsRef,
    wsMapRef
  );

  const wsReconnect = useWebSocketReconnect({
    userAlias,
    wsMapRef,
    addLog: agentPanel.addLog,
    addPipelineLog: agentPanel.addPipelineLog,
    setIsAgentPanelVisible: agentPanel.setIsAgentPanelVisible,
    setJobStatus: agentPanel.setJobStatus,
    setProgressMessage: agentPanel.setProgressMessage,
    setStreamingText: agentPanel.setStreamingText,
    streamingTextRef: agentPanel.streamingTextRef,
    setPersistedEphemeralEntries: agentPanel.setPersistedEphemeralEntries,
    setPipelinePersistedEphemeral: agentPanel.setPipelinePersistedEphemeral,
    setPipelineStreamingText: agentPanel.setPipelineStreamingText,
    pipelineStreamingTextRef: agentPanel.pipelineStreamingTextRef,
    setParallelPipelines: agentPanel.setParallelPipelines,
    setPipelineLogs: agentPanel.setPipelineLogs,
    setActivePipelineTab: agentPanel.setActivePipelineTab,
    setJobHistory: jobTracking.setJobHistory,
    setCurrentJobId: jobTracking.setCurrentJobId,
    currentJobIdRef: jobTracking.currentJobIdRef,
    isJobRunningRef: jobTracking.isJobRunningRef,
    jobHistory: jobTracking.jobHistory,
    selectedLambdas: [] as never[],
    selectedVersions: {},
    accounts: [] as never[],
    currentConfigName: inventory.currentConfig.name,
    getSelectedPipelines: () => [],
    parallelPipelines: agentPanel.parallelPipelines
  });

  // ── Column definitions (memoized) ──
  const lambdaColumnDefinitions = useMemo(() => createLambdaColumnDefinitions(inventory.eolVersionsSet), [inventory.eolVersionsSet]);
  const lambdaColumnDefinitionsWithSelect = useMemo(
    () =>
      createLambdaColumnDefinitionsWithSelect(
        inventory.eolVersionsSet,
        selection.selectedLambdaIdsRef,
        selection.toggleLambdaSelection,
        selection.selectionVersion
      ),
    [inventory.eolVersionsSet, selection.selectedLambdaIdsRef, selection.toggleLambdaSelection, selection.selectionVersion]
  );

  // ── Handlers ──
  const handleStartVersionUpdate = useCallback(() => {
    if (selection.selectedLambdas.length === 0) return;
    const languageCounts = getLambdaCountByLanguage(selection.selectedLambdas);
    const defaultVersions: SelectedVersions = {};
    Object.keys(languageCounts).forEach((lang) => {
      const config = RUNTIME_CONFIGS.find((c) => c.language === lang);
      if (config) {
        const recommended = config.versions.find((v) => v.isRecommended);
        defaultVersions[lang] = recommended?.value || config.versions[0]?.value || '';
      }
    });
    setSelectedVersions(defaultVersions);
    setIsVersionModalOpen(true);
  }, [selection.selectedLambdas]);

  const handleConfirmUpdate = useCallback(() => {
    // Check for concurrent runs on the same pipelines
    const selectedPipelines = selection.getSelectedPipelines();
    const conflicting = selectedPipelines.filter((p) => jobTracking.resourcesInProgress.has(p));

    if (conflicting.length > 0 && conflicting.length === selectedPipelines.length) {
      // All selected pipelines have in-progress jobs — block entirely
      setIsVersionModalOpen(false);
      setConcurrentJobAlert(
        `Cannot start update: ${conflicting.join(', ')} already ${conflicting.length === 1 ? 'has' : 'have'} an in-progress job. Wait for ${
          conflicting.length === 1 ? 'it' : 'them'
        } to complete before starting a new run.`
      );
      return;
    }

    if (conflicting.length > 0) {
      // Some pipelines have in-progress jobs — warn and proceed with the rest
      setConcurrentJobAlert(
        `Skipped ${conflicting.join(', ')} — already ${
          conflicting.length === 1 ? 'has' : 'have'
        } an in-progress job. Proceeding with the remaining pipelines.`
      );
    } else {
      setConcurrentJobAlert(null);
    }

    setIsVersionModalOpen(false);
    jobTracking.setJobExecutionFilter('in-progress');
    filtering.setActiveFilter('job-executions');
    webSocket.handleConfirmUpdate();
  }, [webSocket, jobTracking, filtering, selection]);

  const handleRetryJob = useCallback(
    (job: JobRun) => {
      const { lambdaIdsToSelect, versions } = jobTracking.handleRetryJob(job);
      selection.setSelectedLambdaIds(lambdaIdsToSelect);
      setSelectedVersions(versions);
      setIsVersionModalOpen(true);
    },
    [jobTracking, selection]
  );

  const handleDeleteClick = useCallback(() => {
    // Combine deleted pipeline stack names and manual stack names for stack deletion
    const allStacksToDelete = [...new Set([...selection.deletedPipelineNames, ...selection.manualStackNames])];
    // Collect ARNs of standalone manual lambdas (no CFN stack)
    const manualLambdaArns = selection.manualStandaloneLambdas.map((item) => ({
      arn: item.lambda.arn,
      name: item.lambda.name,
      region: item.lambda.region
    }));

    if (allStacksToDelete.length > 0 || manualLambdaArns.length > 0) {
      jobTracking.setJobExecutionFilter('in-progress');
      filtering.setActiveFilter('job-executions');
      webSocket.handleDeleteStacks(allStacksToDelete, selection.selectedLambdasWithPipeline, manualLambdaArns);
    }
  }, [webSocket, selection, jobTracking, filtering]);

  const handleBrazilStartUpdate = useCallback(() => {
    brazilEOL.setIsBrazilVersionModalOpen(false);
    jobTracking.setJobExecutionFilter('in-progress');
    filtering.setActiveFilter('job-executions');
    webSocket.handleBrazilInterpreterUpdate(brazilEOL.selectedBrazilPipelines, brazilEOL.selectedBrazilVersions, brazilEOL.clearBrazilSelection);
  }, [webSocket, brazilEOL, jobTracking, filtering]);

  // ── Resource type tabs ──
  const enabledConfigs = getEnabledConfigs();
  const resourceTabs = useMemo(
    () =>
      enabledConfigs.map((config) => ({
        id: config.id,
        label: (
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <span>{config.icon}</span>
            <span>{config.name}</span>
          </SpaceBetween>
        ),
        content: null
      })),
    [enabledConfigs]
  );

  // ── Render helpers ──
  const renderResourceContent = () => {
    const { activeFilter } = filtering;

    return (
      <SpaceBetween size="m">
        {/* Dashboard Stats */}
        <DashboardStatsContainer
          currentConfig={inventory.currentConfig}
          isLoading={inventory.isLoading}
          statsData={inventory.statsData}
          isBrazilEOLLoading={inventory.isBrazilEOLLoading}
          brazilEOLData={inventory.brazilEOLData}
          resourceTypeJobs={jobTracking.resourceTypeJobs}
          resourceInProgressJobs={jobTracking.resourceInProgressJobs}
          activeFilter={activeFilter}
          setActiveFilter={filtering.setActiveFilter}
          setIsViewTransitioning={filtering.setIsViewTransitioning}
        />

        {/* Conditional content based on active filter */}
        {activeFilter === 'job-executions' && (
          <JobExecutionsContainer
            currentConfig={inventory.currentConfig}
            userAlias={userAlias}
            resourceInProgressJobs={jobTracking.resourceInProgressJobs}
            resourceCompletedJobs={jobTracking.resourceCompletedJobs}
            jobExecutionFilter={jobTracking.jobExecutionFilter}
            setJobExecutionFilter={jobTracking.setJobExecutionFilter}
            jobFilterQuery={jobTracking.jobFilterQuery}
            setJobFilterQuery={jobTracking.setJobFilterQuery}
            filterJobsByQuery={jobTracking.filterJobsByQuery}
            handleCancelJob={jobTracking.handleCancelJob}
            handleRetryJob={handleRetryJob}
            setSelectedJobForView={jobTracking.setSelectedJobForView}
            setIsAgentPanelVisible={agentPanel.setIsAgentPanelVisible}
            setAgentLogs={agentPanel.setAgentLogs}
            setStreamingText={agentPanel.setStreamingText}
            setProgressMessage={agentPanel.setProgressMessage}
            setCloudWatchPollingJobId={cloudWatch.setCloudWatchPollingJobId}
            currentJobIdRef={jobTracking.currentJobIdRef}
            agentLogs={agentPanel.agentLogs}
            historicalJobTabs={agentPanel.historicalJobTabs}
            setHistoricalJobTabs={agentPanel.setHistoricalJobTabs}
            setHistoricalJobLogs={agentPanel.setHistoricalJobLogs}
            setActivePipelineTab={agentPanel.setActivePipelineTab}
            subscribeToJob={wsReconnect.subscribeToJob}
          />
        )}

        {activeFilter === 'eol' && (
          <EOLRuntimesContainer
            accounts={inventory.accounts}
            statsData={inventory.statsData}
            precomputedEolData={inventory.precomputedEolData}
            eolFilterType={filtering.eolFilterType}
            setEolFilterType={filtering.setEolFilterType}
            searchQuery={filtering.searchQuery}
            handleSearchFilterChange={filtering.handleSearchFilterChange}
            isViewTransitioning={filtering.isViewTransitioning}
            setIsViewTransitioning={filtering.setIsViewTransitioning}
            selectedLambdaIds={selection.selectedLambdaIds}
            setSelectedLambdaIds={selection.setSelectedLambdaIds}
            selectedLambdaIdsRef={selection.selectedLambdaIdsRef}
            togglePipelineSelection={selection.togglePipelineSelection}
            isPipelineSelected={selection.isPipelineSelected}
            isPipelinePartiallySelected={selection.isPipelinePartiallySelected}
            lambdaColumnDefinitions={lambdaColumnDefinitions}
            lambdaColumnDefinitionsWithSelect={lambdaColumnDefinitionsWithSelect}
          />
        )}

        {activeFilter === 'brazil-eol' && (
          <BrazilEOLContainer
            brazilEOLData={inventory.brazilEOLData}
            isBrazilEOLLoading={inventory.isBrazilEOLLoading}
            brazilEOLError={inventory.brazilEOLError}
            brazilEOLRuntimeFilter={brazilEOL.brazilEOLRuntimeFilter}
            setBrazilEOLRuntimeFilter={brazilEOL.setBrazilEOLRuntimeFilter}
            brazilEOLSearchQuery={brazilEOL.brazilEOLSearchQuery}
            handleBrazilEOLSearchFilterChange={brazilEOL.handleBrazilEOLSearchFilterChange}
            selectedBrazilPipelines={brazilEOL.selectedBrazilPipelines}
            toggleBrazilPipelineSelection={brazilEOL.toggleBrazilPipelineSelection}
            isBrazilPipelineSelected={brazilEOL.isBrazilPipelineSelected}
          />
        )}

        {(activeFilter === null || activeFilter === 'all' || activeFilter === 'pipelines' || activeFilter === 'accounts') && (
          <ResourceTableContainer
            accounts={inventory.accounts}
            groupedPipelines={filtering.groupedPipelines}
            filteredAccounts={filtering.filteredAccounts}
            statsData={inventory.statsData}
            viewMode={filtering.viewMode}
            setViewMode={filtering.setViewMode}
            isViewTransitioning={filtering.isViewTransitioning}
            setIsViewTransitioning={filtering.setIsViewTransitioning}
            setActiveFilter={filtering.setActiveFilter}
            handleSearchFilterChange={filtering.handleSearchFilterChange}
            currentPipelinePage={filtering.currentPipelinePage}
            setCurrentPipelinePage={filtering.setCurrentPipelinePage}
            currentAccountPage={filtering.currentAccountPage}
            setCurrentAccountPage={filtering.setCurrentAccountPage}
            PIPELINES_PER_PAGE={filtering.PIPELINES_PER_PAGE}
            ACCOUNTS_PER_PAGE={filtering.ACCOUNTS_PER_PAGE}
            expandedPipelines={filtering.expandedPipelines}
            togglePipelineExpanded={filtering.togglePipelineExpanded}
            selectedLambdaIds={selection.selectedLambdaIds}
            setSelectedLambdaIds={selection.setSelectedLambdaIds}
            selectedLambdaIdsRef={selection.selectedLambdaIdsRef}
            togglePipelineSelection={selection.togglePipelineSelection}
            isPipelineSelected={selection.isPipelineSelected}
            isPipelinePartiallySelected={selection.isPipelinePartiallySelected}
            lambdaColumnDefinitions={lambdaColumnDefinitions}
            lambdaColumnDefinitionsWithSelect={lambdaColumnDefinitionsWithSelect}
          />
        )}
      </SpaceBetween>
    );
  };

  const renderJobExecutionsContent = () => (
    <JobExecutionsContainer
      currentConfig={inventory.currentConfig}
      userAlias={userAlias}
      resourceInProgressJobs={jobTracking.resourceInProgressJobs}
      resourceCompletedJobs={jobTracking.resourceCompletedJobs}
      jobExecutionFilter={jobTracking.jobExecutionFilter}
      setJobExecutionFilter={jobTracking.setJobExecutionFilter}
      jobFilterQuery={jobTracking.jobFilterQuery}
      setJobFilterQuery={jobTracking.setJobFilterQuery}
      filterJobsByQuery={jobTracking.filterJobsByQuery}
      handleCancelJob={jobTracking.handleCancelJob}
      handleRetryJob={handleRetryJob}
      setSelectedJobForView={jobTracking.setSelectedJobForView}
      setIsAgentPanelVisible={agentPanel.setIsAgentPanelVisible}
      setAgentLogs={agentPanel.setAgentLogs}
      setStreamingText={agentPanel.setStreamingText}
      setProgressMessage={agentPanel.setProgressMessage}
      setCloudWatchPollingJobId={cloudWatch.setCloudWatchPollingJobId}
      currentJobIdRef={jobTracking.currentJobIdRef}
      agentLogs={agentPanel.agentLogs}
      historicalJobTabs={agentPanel.historicalJobTabs}
      setHistoricalJobTabs={agentPanel.setHistoricalJobTabs}
      setHistoricalJobLogs={agentPanel.setHistoricalJobLogs}
      setActivePipelineTab={agentPanel.setActivePipelineTab}
      subscribeToJob={wsReconnect.subscribeToJob}
    />
  );

  // ── Main return ──
  return (
    <AppLayout
      contentType="cards"
      navigation={<HomePageSideNavigationV2 />}
      toolsHide={true}
      content={
        <div className={styles.pageContainer}>
          {/* Main Header */}
          <Container
            disableContentPaddings
            header={
              <Header variant="h1" description="Centralized AWS resource version management system using Agentic AI workflows">
                Upgrade Medic
              </Header>
            }
          />

          {/* Resource Type Tabs */}
          {concurrentJobAlert && (
            <Alert type="error" dismissible onDismiss={() => setConcurrentJobAlert(null)}>
              {concurrentJobAlert}
            </Alert>
          )}
          <Box padding={{ top: 'xs' }}>
            <Tabs
              activeTabId={inventory.activeResourceType}
              onChange={({ detail }) => {
                inventory.setActiveResourceType(detail.activeTabId);
                filtering.setActiveFilter(null);
                selection.clearSelection();
              }}
              tabs={resourceTabs}
            />
          </Box>

          {/* Resource-specific Content */}
          <Box>
            {inventory.isLoading ? (
              <Container>
                <Box textAlign="center" padding={{ vertical: 'xxl' }}>
                  <SpaceBetween size="m" alignItems="center">
                    <StatusIndicator type="loading">Loading Lambda inventory from DynamoDB...</StatusIndicator>
                    <Box color="text-body-secondary" fontSize="body-s">
                      This may take a few seconds for large inventories
                    </Box>
                  </SpaceBetween>
                </Box>
              </Container>
            ) : inventory.dataError ? (
              <Container>
                <Box textAlign="center" padding={{ vertical: 'xxl' }}>
                  <SpaceBetween size="m" alignItems="center">
                    <StatusIndicator type="error">{inventory.dataError}</StatusIndicator>
                    <Button onClick={() => window.location.reload()}>Retry</Button>
                  </SpaceBetween>
                </Box>
              </Container>
            ) : inventory.activeResourceType === 'job-executions' ? (
              renderJobExecutionsContent()
            ) : (
              renderResourceContent()
            )}
          </Box>

          {/* ── Modals ── */}
          <VersionSelectionModal
            visible={isVersionModalOpen}
            onDismiss={() => setIsVersionModalOpen(false)}
            currentConfig={inventory.currentConfig}
            selectedLambdasCount={selection.selectedLambdas.length}
            selectedLambdas={selection.selectedLambdas}
            selectedLanguageCounts={selection.selectedLanguageCounts}
            selectedVersions={selectedVersions}
            setSelectedVersions={setSelectedVersions}
            deploymentOptions={deploymentOptions}
            setDeploymentOptions={setDeploymentOptions}
            getSelectedPipelines={selection.getSelectedPipelines}
            onConfirm={handleConfirmUpdate}
          />

          <InstallInstructionsModal visible={isInstallModalOpen} onDismiss={() => setIsInstallModalOpen(false)} />

          <BrazilVersionModal
            visible={brazilEOL.isBrazilVersionModalOpen}
            onDismiss={() => brazilEOL.setIsBrazilVersionModalOpen(false)}
            selectedBrazilPipelines={brazilEOL.selectedBrazilPipelines}
            selectedBrazilVersions={brazilEOL.selectedBrazilVersions}
            setSelectedBrazilVersions={brazilEOL.setSelectedBrazilVersions}
            onStartUpdate={handleBrazilStartUpdate}
          />

          {/* ── Sticky Action Bar: Brazil EOL Pipelines ── */}
          {brazilEOL.selectedBrazilPipelines.size > 0 &&
            filtering.activeFilter === 'brazil-eol' &&
            (!agentPanel.isAgentPanelVisible || agentPanel.isAgentPanelMinimized) &&
            !brazilEOL.isBrazilVersionModalOpen && (
              <div
                className={styles.stickyActionBar}
                style={{
                  backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                  boxShadow: isDarkMode ? '0 -2px 8px rgba(0, 0, 0, 0.3)' : '0 -2px 8px rgba(0, 0, 0, 0.1)',
                  bottom: agentPanel.isAgentPanelMinimized ? '42px' : '0'
                }}
              >
                <SpaceBetween direction="horizontal" size="m" alignItems="center">
                  <Box fontWeight="bold">{brazilEOL.selectedBrazilPipelines.size} Pipeline(s) selected</Box>
                  <Button variant="primary" onClick={brazilEOL.openBrazilVersionModal} disabled={!jobTracking.canStartNewJob}>
                    {jobTracking.canStartNewJob
                      ? '▶ Start Interpreter Update'
                      : `⚠️ ${jobTracking.runningJobCount}/${jobTracking.MAX_CONCURRENT_JOBS} Jobs Running`}
                  </Button>
                  <Button variant="normal" onClick={brazilEOL.clearBrazilSelection}>
                    Clear Selection
                  </Button>
                </SpaceBetween>
              </div>
            )}

          {/* ── Sticky Action Bar: Lambda Selection ── */}
          {selection.selectedLambdas.length > 0 &&
            (!agentPanel.isAgentPanelVisible || agentPanel.isAgentPanelMinimized) &&
            !isVersionModalOpen &&
            filtering.activeFilter !== 'brazil-eol' && (
              <div
                className={styles.stickyActionBar}
                style={{
                  backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                  boxShadow: isDarkMode ? '0 -2px 8px rgba(0, 0, 0, 0.3)' : '0 -2px 8px rgba(0, 0, 0, 0.1)',
                  bottom: agentPanel.isAgentPanelMinimized ? '42px' : '0'
                }}
              >
                <SpaceBetween direction="horizontal" size="m" alignItems="center">
                  <Box fontWeight="bold">{selection.selectedLambdas.length} Lambda(s) selected</Box>
                  <Button variant="primary" onClick={handleStartVersionUpdate} disabled={!jobTracking.canStartNewJob}>
                    {jobTracking.canStartNewJob
                      ? '▶ Start Version Update'
                      : `⚠️ ${jobTracking.runningJobCount}/${jobTracking.MAX_CONCURRENT_JOBS} Jobs Running`}
                  </Button>
                  <span
                    title={
                      !selection.canDeleteSelected && selection.pipelineLambdaCount > 0
                        ? `Delete is only available for manually created lambdas or deleted pipelines. ${selection.pipelineLambdaCount} active pipeline-managed lambda(s) selected - deselect them to enable delete.`
                        : selection.canDeleteSelected
                        ? selection.deletedPipelineLambdaCount > 0
                          ? `Delete ${selection.deletedPipelineNames.length} deleted pipeline stack(s)${
                              selection.manualStackNames.length > 0 ? `, ${selection.manualStackNames.length} manual stack(s)` : ''
                            } and ${selection.manualLambdaCount} manually created lambda(s)`
                          : selection.manualStackNames.length > 0 && selection.manualStandaloneLambdas.length > 0
                          ? `Delete ${selection.manualStackNames.length} manual stack(s) and ${selection.manualStandaloneLambdas.length} manually created lambda(s)`
                          : selection.manualStackNames.length > 0
                          ? `Delete ${selection.manualStackNames.length} manual stack(s)`
                          : `Delete ${selection.manualLambdaCount} manually created lambda(s)`
                        : ''
                    }
                  >
                    <Button variant="normal" onClick={handleDeleteClick} disabled={!selection.canDeleteSelected}>
                      🗑️ Delete
                    </Button>
                  </span>
                  <Button variant="normal" onClick={selection.clearSelection}>
                    Clear Selection
                  </Button>
                </SpaceBetween>
              </div>
            )}

          {/* ── Agent Output Panel ── */}
          {agentPanel.isAgentPanelVisible && (
            <AgentPanelContainer
              isDarkMode={isDarkMode}
              panelHeight={agentPanel.panelHeight}
              setPanelHeight={agentPanel.setPanelHeight}
              setIsResizing={agentPanel.setIsResizing}
              setIsAgentPanelVisible={agentPanel.setIsAgentPanelVisible}
              isAgentPanelMinimized={agentPanel.isAgentPanelMinimized}
              setIsAgentPanelMinimized={agentPanel.setIsAgentPanelMinimized}
              jobStatus={agentPanel.jobStatus}
              progressMessage={agentPanel.progressMessage}
              filteredAgentLogs={agentPanel.filteredAgentLogs}
              streamingText={
                agentPanel.activePipelineTab.startsWith('history-')
                  ? ''
                  : agentPanel.pipelineStreamingText[agentPanel.activePipelineTab] || agentPanel.streamingText
              }
              persistedEphemeralEntries={agentPanel.persistedEphemeralEntries}
              setPersistedEphemeralEntries={agentPanel.setPersistedEphemeralEntries}
              pipelinePersistedEphemeral={agentPanel.pipelinePersistedEphemeral}
              setPipelinePersistedEphemeral={agentPanel.setPipelinePersistedEphemeral}
              activePipelineTab={agentPanel.activePipelineTab}
              setActivePipelineTab={agentPanel.setActivePipelineTab}
              parallelPipelines={agentPanel.parallelPipelines}
              setParallelPipelines={agentPanel.setParallelPipelines}
              agentLogs={agentPanel.agentLogs}
              pipelineLogs={agentPanel.pipelineLogs}
              setPipelineLogs={agentPanel.setPipelineLogs}
              historicalJobTabs={agentPanel.historicalJobTabs}
              setHistoricalJobTabs={agentPanel.setHistoricalJobTabs}
              setHistoricalJobLogs={agentPanel.setHistoricalJobLogs}
              logsContainerRef={agentPanel.logsContainerRef}
              handleLogsScroll={agentPanel.handleLogsScroll}
              isAutoRefreshing={cloudWatch.isAutoRefreshing}
              cloudWatchPollingJobId={cloudWatch.cloudWatchPollingJobId}
            />
          )}
        </div>
      }
    />
  );
};
